/**
 * Admin + agent surface types shared between backend, web-admin, agent.
 * Phase 4.
 */
import type { AdminMsgTarget, LedStateName } from "./datachannel.js";
import type { Locale } from "./api.js";
import type { Role, RoomVisibility, UserRole } from "./api.js";

export type { Role, UserRole } from "./api.js";

// ----- Log levels (canonical) -----

/** Canonical, ordered log-verbosity levels used across all logging surfaces. */
export type LogLevel = "off" | "error" | "warn" | "info" | "debug" | "trace";

/** All log levels, ordered least → most verbose (index = severity ordinal). */
export const LOG_LEVELS: LogLevel[] = ["off", "error", "warn", "info", "debug", "trace"];

// ----- Agent → Backend payloads (matches docs/agent/protocol.md §3) -----

export interface AgentNetwork {
  backend_ok: boolean;
  stun_ok: boolean | null;
  rtt_ms: number | null;
}

export interface ThermalReading {
  temp_c: number | null;
  temp_crit_c: number | null;
  temp_max_c: number | null;
  fan_rpm: number | null;
  throttled: boolean | null;
  source: string | null;
}

export interface AgentHeartbeat {
  ts: number;
  agent_version: string;
  state: string;
  network: AgentNetwork;
  led_state: LedStateName;
  led_color: string;
  led_blink: boolean;
  on_air: boolean;
  kiosk_alive: boolean;
  /**
   * Whether the LED/on-air hardware link was live at heartbeat time. Optional:
   * older agents omit it, in which case the link status is UNKNOWN — consumers
   * must NOT render an absent value as connected.
   */
  led_connected?: boolean;
  /**
   * Wall-clock ms when the LED link was last observed live (null = not since
   * the agent started). Treat a value older than ~3 heartbeat intervals as
   * stale rather than "last known good".
   */
  led_seen_ms?: number | null;
  /** Exact release tag (vX.Y.Z) the device is running. Optional (older agents). */
  installed_tag?: string;
  /** Commit sha the device is running. Optional. */
  installed_commit?: string;
  /** Release/commit date the device is running (ISO string). Optional. */
  release_time?: string;
  /** Latest thermal reading from the device. Optional. */
  thermal?: ThermalReading;
}

export interface MediaCaps {
  schema_version: number;
  probed_ts: number;
  agent_version: string;
  hw_signature: string;
  gpu: {
    vendor_id: string;
    device_id: string;
    driver: string;
    render_node: string | null;
    va_version: string | null;
  } | null;
  va_profiles: Array<{
    profile: string;
    entrypoints: string[];
  }>;
  cpu: {
    model: string;
    physical_cores: number;
    logical_cores: number;
    flags: string[];
    has_avx2: boolean;
  };
  bench: {
    logic_version: number;
    ran_ts: number;
    results: Array<{
      codec: string;
      width: number;
      height: number;
      fps: number;
      speed_x: number;
      cpu_s_per_video_s: number;
    }>;
  } | null;
  cameras: Array<{
    path: string;
    label: string;
    max_width: number;
    max_height: number;
    max_fps: number;
    sustained_ok: boolean | null;
  }>;
  thermal: ThermalReading | null;
  cache_hit: boolean;
}

export interface MediaProfile {
  rung: 1 | 2 | 3;
  tier: "A" | "B" | "C";
  codec: "h264" | "vp8" | "vp9";
  performance_level: "silent" | "quiet" | "normal" | "max";
  roles: Record<
    string,
    {
      role: "board" | "face" | "auto";
      width: number;
      height: number;
      fps: number;
      content_hint: "text" | "motion" | "detail";
      degradation_preference: "maintain-resolution" | "maintain-framerate";
    }
  >;
  why: string[];
  resolved_ts: number;
}

export interface MediaCalibration {
  ts: number;
  encoder_implementation: string;
  power_efficient_encoder: boolean | null;
  quality_limitation_reason: string | null;
  ms_per_frame: number | null;
  codec: string;
}

/**
 * One pixel format a V4L2 camera advertises, with the frame sizes (and the
 * frame rates per size) it supports. Populated best-effort from
 * `v4l2-ctl --list-formats-ext`; absent on older agents / hosts without the
 * tool. Surfaced in the admin camera details panel.
 */
export interface EnumCameraSize {
  width: number;
  height: number;
  /** Supported frame rates (fps) for this size; may be empty if unreadable. */
  fps: number[];
}
export interface EnumCameraFormat {
  /** FOURCC pixel format code, e.g. "MJPG", "YUYV", "H264". */
  fourcc: string;
  /** Human-readable description from V4L2, e.g. "Motion-JPEG". */
  description?: string;
  sizes: EnumCameraSize[];
}

export interface EnumCamera {
  /** Linux device node, e.g. `/dev/video0`. */
  path: string;
  /** Bare node name, e.g. `video0`. */
  name: string;
  /**
   * Human-readable V4L2 card name from sysfs (e.g. "HD Pro Webcam C920").
   * Used to bridge between the Linux `/dev/video*` selection and the browser's
   * opaque `MediaDeviceInfo.deviceId` — web-device matches by label substring.
   * Null when sysfs not available or unreadable.
   */
  label?: string | null;
  /**
   * True when the V4L2 node advertises `V4L2_CAP_VIDEO_CAPTURE`. UVC cameras
   * expose multiple `/dev/videoN` nodes per physical device; only one is the
   * actual capture node the browser opens. Older agents may omit; treat
   * `undefined` as "unknown — show it".
   */
  capture?: boolean;
  /**
   * Pixel formats / resolutions / frame rates the camera advertises (V4L2).
   * Best-effort: absent on older agents or hosts without `v4l2-ctl`.
   */
  formats?: EnumCameraFormat[];
}
export interface EnumDisplay {
  name: string;
  status: string;
}
export interface EnumSerial {
  path: string;
  name: string;
}
export interface EnumUsb {
  vid: string;
  pid: string;
  desc: string;
}
export interface EnumAudio {
  name: string;
  max_input_channels: number;
  max_output_channels: number;
}

/**
 * One ALSA sound card as seen by the agent (root, owns ALSA). Surfaced so a
 * remote operator can pick the speaker/mic CARD — the most reliable selector on
 * devices where the ALSA default resolved to a capture-only mic. Best-effort;
 * absent (empty) on older agents or non-Linux hosts.
 */
export interface EnumAudioCard {
  /** Kernel card index. Unstable across USB enumeration — prefer `id`. */
  index: number;
  /** Short stable card id from /proc/asound/cards, e.g. "PCH". */
  id: string;
  /** Long human-readable card name. */
  name: string;
  /** True when the card exposes a playback device/control. */
  playback: boolean;
  /** True when the card exposes a capture device. */
  capture: boolean;
  /** Simple-mixer control names (lower-case), e.g. ["master","pcm"]. */
  controls: string[];
}

export interface AgentSystemGit {
  tag: string | null;
  hash: string | null;
  commit_date: string | null;
  branch: string | null;
}
export interface AgentSystemOs {
  id: string | null;
  pretty_name: string | null;
  version_id: string | null;
  kernel: string | null;
  last_update_ts: number | null;
}
export interface AgentSystemInfo {
  git: AgentSystemGit;
  os: AgentSystemOs;
  channel: string | null;
}

export interface AgentEnumeration {
  cameras: EnumCamera[];
  displays: EnumDisplay[];
  serial: EnumSerial[];
  usb: EnumUsb[];
  audio: EnumAudio[];
  /**
   * ALSA cards (playback/capture caps) discovered by the agent. Best-effort;
   * absent on older agents / non-Linux hosts. Used by admin to pick the speaker
   * + mic CARD remotely (persisted as `DeviceConfig.audioCard`).
   */
  audio_cards?: EnumAudioCard[];
  platform: string;
  system?: AgentSystemInfo;
}

// ----- Agent endpoint response shape (Phase 4: empty pending commands ok) -----

export interface AgentCommand {
  /** Opaque command id; agent must echo back when acknowledging. */
  id: string;
  /**
   * Free-form command type. Valid values now include `admin_msg`, `led_test`,
   * `speaker_test`, `volume_set`, `reboot`, `update`, `change_branch`,
   * `set_pin`, `led_board_set`, `flash_firmware`, `set_log_level`.
   */
  type: string;
  payload: unknown;
}

/**
 * Payload for the `set_log_level` device command. Tells the device which
 * effective verbosity to run at; `session_id` correlates the change with an
 * admin debug session (omitted ⇒ a standing/default level change).
 */
export interface SetLogLevelCommandPayload {
  level: LogLevel;
  session_id?: number;
}

export interface AgentHeartbeatAck {
  ok: true;
  /** Server-controlled commands the agent should pick up. */
  commands: AgentCommand[];
}

export interface AgentEnumerationAck {
  ok: true;
}

// ----- Admin dashboard -----

export interface DashboardDeviceState {
  /** Last heartbeat timestamp (ms epoch) or null if none received yet. */
  last_heartbeat_ts: number | null;
  /** Seconds since last heartbeat, null if none yet. */
  last_heartbeat_age_s: number | null;
  /** Last device enumeration timestamp, null until first enumeration arrives. */
  enumeration_ts?: number | null;
  agent_version: string | null;
  agent_state: string | null;
  network: AgentNetwork | null;
  led_state: LedStateName | null;
  led_color: string | null;
  led_blink: boolean | null;
  on_air: boolean;
  kiosk_alive: boolean;
  /**
   * LED hardware link status as of the last heartbeat: true/false when the
   * agent reported it, null when UNKNOWN (older agent, or no heartbeat yet).
   * Renderers must show "unknown", never "connected", for null.
   */
  led_connected: boolean | null;
  /** Last ms epoch at which the LED link was observed live; null = never/unknown. */
  led_seen_ms: number | null;
  /** Aggregate enumeration summary; null until first enumeration arrives. */
  enumeration: AgentEnumeration | null;
  /** Device token label (which device this is). */
  device_label: string | null;
}

export interface DashboardResponse {
  ok: true;
  device: DashboardDeviceState;
  generated_at: number;
}

// ----- Admin live dashboard (monitoring) -----

/**
 * A single actionable problem surfaced in the dashboard Attention/Alerts panel.
 * `href` points at the relevant admin route so the line can be clicked through.
 */
export interface AttentionItem {
  kind: string;
  message: string;
  href: string;
  severity: "info" | "warn" | "error";
}

/** An online device row in the live dashboard. */
export interface DashboardDevice {
  label: string;
  /** online_since heartbeat timestamp (ms epoch); for uptime. */
  online_since: number;
  room_id: number | null;
  room_name: string | null;
  school_id: string | null;
  school_name: string | null;
  /** force_encryption || e2ee_key_id present. */
  encrypted: boolean;
  update_available: boolean;
  /** LiveKit student identities currently in the device's bound room. */
  students_connected: string[];
  /** online && has room && 0 students connected. */
  idle: boolean;
}

/** A connected student row in the live dashboard. */
export interface DashboardStudent {
  id: string;
  display_name: string;
  email: string;
  room_id: number | null;
  room_name: string | null;
  school_id: string | null;
  school_name: string | null;
  /** Device bound to the student's room (if any). */
  device_label: string | null;
  /** LiveKit participant joinedAt (ms epoch), or null if unknown. */
  connected_since: number | null;
}

/** Aggregated live-monitoring snapshot for the admin dashboard. */
export interface DashboardLive {
  /** Server timestamp for "checked"/"updated". */
  generated_at: number;
  devices_total: number;
  devices_online: number;
  /** Rooms with >=1 online device. */
  rooms_active: number;
  /** Distinct connected students across rooms. */
  students_connected: number;
  /** Devices with an update pending. */
  updates_available: number;
  encrypted: { enabled: number; total: number };
  health: { status: "ok" | "warn" | "fail"; checked_at: number };
  attention: AttentionItem[];
  online_devices: DashboardDevice[];
  connected_students: DashboardStudent[];
}

// ----- Admin logs -----

/** Audit severity (subset of diagnostic log levels). */
export type AuditLevel = "info" | "warn" | "error";

export interface AuditEntry {
  id: number;
  /** Acting user (actor) id, or null for system/device-initiated events. */
  user_id: number | null;
  /** Actor email (wire field historically named `username`). */
  username: string | null;
  event: string;
  payload: unknown;
  ts: number;
  /** Severity: info | warn | error. Defaults to "info" on older backends. */
  level?: AuditLevel;
  /** Device this event relates to (label), or null. Absent on older backends. */
  device_id?: string | null;
  /** Subject user the action targets (distinct from actor), or null. */
  target_user_id?: number | null;
  /** Resolved subject-user email for display, or null. */
  target_username?: string | null;
  /** School this event relates to, or null. */
  school_id?: number | null;
  /** Resolved school name for display, or null. */
  school_name?: string | null;
  /** Room this event relates to, or null. */
  room_id?: number | null;
  /** Resolved room name for display, or null. */
  room_name?: string | null;
}

export interface AdminLogsResponse {
  ok: true;
  entries: AuditEntry[];
  /** Next `since` cursor (largest id returned). */
  next_cursor: number | null;
  /** Total rows currently in audit_log (approx). */
  total: number;
}

export interface AuditEventsResponse {
  ok: true;
  events: string[];
}

// ----- Authorities + Schools (multi-tenant) -----

export interface Authority {
  id: number;
  name: string;
  /**
   * When true, students under this authority may NOT use invisible mode. The
   * effective forbid for a student is the OR of authority/school/room/device
   * flags along the join chain.
   */
  forbid_invisible: boolean;
  /**
   * When true, students/devices under this authority must perform in-person QR
   * key exchange (auto-seal disabled). The effective value for a room is the OR
   * of authority/school/room/device flags along the join chain.
   */
  force_e2ee_qr: boolean;
  /**
   * When true, end-to-end encryption is REQUIRED for all devices/students
   * under this authority (no opt-out, unsupported viewers blocked); auto-seal
   * key distribution stays enabled. Effective value for a room is the OR of
   * authority/school/room/device flags along the join chain.
   */
  force_e2ee: boolean;
  /**
   * E2EE media-key rotation hour (0-23 local), or null = inherit. The effective
   * value for a room is the MOST-SPECIFIC set level: device → room → school →
   * authority → default 0.
   */
  e2ee_rotate_hour: number | null;
  created_at: number;
}

export interface AuthoritiesResponse {
  ok: true;
  authorities: Authority[];
}

export interface CreateAuthorityRequest {
  name: string;
}

export interface UpdateAuthorityRequest {
  name?: string;
  /** Forbid invisible mode for all students under this authority. */
  forbid_invisible?: boolean;
  /** Force QR key exchange for all devices/students under this authority. */
  force_e2ee_qr?: boolean;
  /** Require E2EE (auto-seal kept) for all devices/students under this authority. */
  force_e2ee?: boolean;
  /** E2EE media-key rotation hour (0-23), or null to clear/inherit. */
  e2ee_rotate_hour?: number | null;
}

export interface School {
  id: number;
  name: string;
  authority_id: number;
  authority_name?: string | null;
  description: string | null;
  /** Forbid invisible mode for all students in this school. */
  forbid_invisible: boolean;
  /** Force QR key exchange for all devices/students in this school. */
  force_e2ee_qr: boolean;
  /** Require E2EE (auto-seal kept) for all devices/students in this school. */
  force_e2ee: boolean;
  /** E2EE media-key rotation hour (0-23 local), or null = inherit. */
  e2ee_rotate_hour: number | null;
  created_at: number;
}

export interface SchoolsResponse {
  ok: true;
  schools: School[];
}

export interface CreateSchoolRequest {
  name: string;
  authority_id: number;
  description?: string | null;
}

export interface UpdateSchoolRequest {
  name?: string;
  description?: string | null;
  /** Forbid invisible mode for all students in this school. */
  forbid_invisible?: boolean;
  /** Force QR key exchange for all devices/students in this school. */
  force_e2ee_qr?: boolean;
  /** Require E2EE (auto-seal kept) for all devices/students in this school. */
  force_e2ee?: boolean;
  /** E2EE media-key rotation hour (0-23), or null to clear/inherit. */
  e2ee_rotate_hour?: number | null;
}

// ----- Admin users CRUD (email + roles) -----

export interface AdminUser {
  id: number;
  email: string;
  display_name: string | null;
  full_name: string | null;
  roles: UserRole[];
  disabled: boolean;
  email_verified: boolean;
  created_at: number;
  /** Effective log verbosity for this user. Always provided by the backend. */
  loglevel: LogLevel;
  /** Preferred UI language for the student app. Always present (default "de"). */
  language: Locale;
  /**
   * Whether the user has a profile image set. The image bytes are NOT inlined in
   * this list shape (payload size); staff fetch them via
   * `GET /students/:idOrEmail/profile-image`.
   */
  has_profile_image: boolean;
  /**
   * Whether the user must change their password on first login (admin created
   * them with an initial password). Absent on older backends ⇒ treat as false.
   */
  must_change_password?: boolean;
  /**
   * Maximum number of concurrent sessions/devices this user may have logged in.
   * Always present (default 1). Absent on older backends ⇒ treat as 1.
   */
  max_sessions?: number;
}

export interface AdminUsersResponse {
  ok: true;
  users: AdminUser[];
}

export interface AdminUserSession {
  /** Epoch ms the session row was created (login time). */
  created_at: number;
  ip: string | null;
  /** Parsed from user-agent; null when unknown. */
  browser: string | null;
  device: string | null;
}

export interface AdminUserSessionsResponse {
  ok: true;
  /** Newest session created_at, or null when no sessions. */
  last_login: number | null;
  sessions: AdminUserSession[];
}

/** A role assignment to grant on user create / via the add-role endpoint. */
export interface RoleGrant {
  role: Role;
  authority_id?: number | null;
  school_id?: number | null;
}

export interface CreateUserRequest {
  email: string;
  /**
   * Initial password. OPTIONAL for all roles EXCEPT admin (admin still requires
   * a password). When omitted for a non-admin role, the user is created with no
   * usable password and a set-password email (reset link) is sent so they
   * choose their own. When provided (non-admin), the user is created verified,
   * flagged must_change_password, and a welcome email is sent. For admin, a
   * password is REQUIRED and behavior is unchanged (no welcome, no forced change).
   */
  password?: string;
  display_name?: string | null;
  /** Role assignments to attach to the new user. */
  roles: RoleGrant[];
  /** Preferred student-app UI language. Optional; defaults to "de" server-side. */
  language?: Locale;
  /** Skip email-verification (sets email_verified). Verification SMTP is later. */
  skip_verification?: boolean;
}

export interface UpdateUserRequest {
  /** Reset password (new plain text). Omit to leave unchanged. */
  password?: string;
  display_name?: string | null;
  /** Full/real name. null clears it; omit to leave unchanged. */
  full_name?: string | null;
  disabled?: boolean;
  /** Change preferred student-app UI language. Omit to leave unchanged. */
  language?: Locale;
  /**
   * Maximum number of concurrent sessions/devices. Integer 1..50. Omit to leave
   * unchanged.
   */
  max_sessions?: number;
}

export interface AddRoleRequest {
  role: Role;
  authority_id?: number | null;
  school_id?: number | null;
}

export interface AdminUserResponse {
  ok: true;
  user: AdminUser;
}

// ----- Admin message push -----

export interface AdminMessageRequest {
  text: string;
  /**
   * Targets to display on. Omitted/empty ⇒ both kiosk and student (back-compat with
   * pre-targets admin clients). The device-app filters delivery per target.
   */
  targets?: AdminMsgTarget[];
}
export interface AdminMessageResponse {
  ok: true;
  id: number;
}

// ----- LED testmode (live, queued for next heartbeat drain) -----

/** Drive a raw color/blink on the LED, held until the next state change. */
export interface LedTestManualRequest {
  mode: "manual";
  /** false → LED off. */
  on: boolean;
  /** RRGGBB hex; required when on=true. */
  color?: string;
  blink?: boolean;
}

/** Show a state's appearance for a few seconds, then restore. */
export interface LedTestDemoRequest {
  mode: "demo";
  state: LedStateName;
  /** Seconds to hold the demo appearance (default 5, max 60). */
  duration_s?: number;
}

export type LedTestRequest = LedTestManualRequest | LedTestDemoRequest;

export interface LedTestResponse {
  ok: true;
  id: number;
}

// ----- Speaker test (live, queued for next heartbeat drain) -----

/**
 * Ask the device to play a short test tone through a speaker so the operator
 * can confirm the selection produces sound. `speaker` is the audio-device
 * NAME the operator currently has selected (matches `DeviceConfig.speaker` /
 * an enumeration `EnumAudio.name`). Omitted / "auto" / unknown → the device
 * plays on its default output. Sending the selected name lets the test reflect
 * an unsaved dropdown choice.
 */
export interface SpeakerTestRequest {
  speaker?: string;
}

export interface SpeakerTestResponse {
  ok: true;
  id: number;
  /** The resolved speaker label sent to the device, or null for default. */
  speaker_label: string | null;
}

// ----- Device config -----

/**
 * One configured classroom camera. The operator enables one or more; the kiosk
 * publishes a LiveKit video track per enabled entry and the student switches between
 * them by `name`.
 */
export interface CameraEntry {
  /** Linux device node path (matches an `EnumCamera.path`). Stable id. */
  path: string;
  /** Operator-assigned display name shown to the student in the switcher. */
  name: string;
  /** Whether the kiosk should publish this camera. */
  enabled: boolean;
}

/**
 * Which on-device backend feeds the "external source" v4l2loopback device (the
 * digital-board / cast receiver that surfaces as an extra camera the student can
 * select). The receiver writes a `/dev/videoN` loopback node which then flows
 * through the normal camera pipeline (enumeration → TokenCamera → kiosk track →
 * student switcher) with no protocol change.
 *
 * - `disabled`            — no receiver; the loopback is fed a placeholder frame
 *                           so the node stays enumerable but shows an idle card.
 * - `official_deb`        — run the vendor's closed EShare desktop client headless
 *                           (under Xvfb), TV-Mirror the board, capture its window.
 * - `opensource_headless` — run our own headless EShare protocol client that writes
 *                           the received H.264 straight into the loopback (Phase 2).
 *
 * Switching backend is a live `external_source_set` command to the agent, which
 * rewrites /etc/diy-avatar/external-source.conf and restarts external-source.service.
 */
export type ExternalSourceBackend =
  | "disabled"
  | "official_deb"
  | "opensource_headless";

export interface DeviceConfig {
  /**
   * @deprecated Single-camera back-compat. Prefer `cameras`. Migrated into
   * `cameras` on load when `cameras` is empty.
   */
  camera: string;
  /**
   * All configured cameras with per-camera name + enabled flag. Multiple may be
   * enabled simultaneously (multi-stream). Empty ⇒ fall back to legacy `camera`.
   */
  cameras: CameraEntry[];
  /**
   * @deprecated Audio-device NAME (PortAudio/sounddevice PCM). Superseded by the
   * card-based `audioCardIn` selector (admin/kiosk both pick physical ALSA
   * cards). Kept for back-compat (resolveMicLabel still reads it); the UI no
   * longer writes named PCMs here, so it stays "auto" → no `mic` in the token →
   * the kiosk uses the ALSA default (now the selected capture card).
   */
  mic: string;
  /**
   * @deprecated Audio-device NAME (PortAudio/sounddevice PCM). Superseded by the
   * card-based `audioCard` selector. Kept for back-compat (resolveSpeakerLabel
   * still reads it); the UI no longer writes named PCMs here, so it stays "auto"
   * → no `speaker` in the token → the kiosk uses the ALSA default (now the
   * selected playback card).
   */
  speaker: string;
  monitor: string;
  /**
   * ALSA card (index or stable id like "PCH") the agent pins as the default
   * playback/control card via /etc/asound.conf, so Chromium/aplay/amixer target
   * the real speaker even when the kernel ALSA default is a capture-only mic.
   * Empty ⇒ the agent keeps its boot-time auto-selection. Matches an
   * `EnumAudioCard.id`/index. Applied live via an `audio_card_set` command and
   * persisted on the device (survives reboot).
   */
  audioCard: string;
  /**
   * ALSA INPUT/capture card (index or stable id) the agent pins as the default
   * capture card. Empty ⇒ capture follows the output card (`audioCard`). When
   * set AND different from `audioCard`, the agent writes an `asym` default in
   * /etc/asound.conf so a separate USB mic and the speaker can both be the ALSA
   * default at once. Matches an `EnumAudioCard.id`/index. Applied live via the
   * `audio_card_set` command (alongside `audioCard`) and persisted on-device.
   */
  audioCardIn: string;
  led_backend: "esp32_serial" | "blinkstick" | "none";
  /**
   * Which ESP firmware the updater (`install-device.sh`) flashes. Persisted on
   * the device at `/etc/diy-avatar/led-board` as a bare single-line token.
   * Applied live via a `led_board_set` command. Default `esp8266`.
   */
  led_board: "esp8266" | "esp32";
  led_port: string;
  /**
   * Classroom speaker output volume in [0, 1]. Applied by the kiosk as
   * `HTMLMediaElement.volume` on the remote-audio element that plays student
   * audio. Defaults to 1 (100%). Independent of `speaker` (sink routing).
   */
  volume: number;
  /**
   * OS hardware master output volume in [0,100]; default 100. Distinct from
   * `volume` which is the LiveKit remote-audio element gain in [0,1].
   */
  masterVolume: number;
  /**
   * External cast-source backend feeding the v4l2loopback "digital board" camera.
   * Default `disabled`. Applied live via an `external_source_set` command and
   * persisted on the device at /etc/diy-avatar/external-source.conf (survives
   * reboot; external-source.service reads it at boot).
   */
  externalSourceBackend: ExternalSourceBackend;
  /**
   * Digital-board IP the EShare client connects to for TV-Mirror. Empty ⇒ the
   * client falls back to auto-discovery by `externalSourceBoardName`. Plain LAN
   * address; no secret. Max 64 chars.
   */
  externalSourceBoardIp: string;
  /**
   * Digital-board display name (as the board advertises it) used for EShare
   * auto-discovery when no IP is set, and for the captured camera's label.
   * Max 64 chars.
   */
  externalSourceBoardName: string;
  /**
   * Download URL for the official EShare desktop client archive the device
   * fetches+extracts for the `official_deb` backend. Defaults to the
   * heinekingmedia Ubuntu build. Operator-overridable so a different vendor
   * board can supply its own client. Max 512 chars.
   */
  externalSourceClientUrl: string;
  /**
   * Media performance profile picked by the operator, biasing the kiosk's
   * publish quality vs. CPU/fan-noise tradeoff:
   * - `silent` — lowest CPU (quietest fans), lowest quality.
   * - `quiet`  — conservative default for classroom laptops.
   * - `normal` — balanced.
   * - `max`    — highest quality, no thermal restraint.
   *
   * Optional; unset ⇒ the backend resolves it to `"quiet"`
   * (see `resolveMediaConfig`). Never required so existing devices keep working.
   */
  mediaPerformanceLevel?: "silent" | "quiet" | "normal" | "max";
  /**
   * Force a specific published video codec instead of letting LiveKit negotiate.
   * `null`/unset ⇒ auto-negotiate. A corrupt/unknown value resolves back to
   * `null` (auto) rather than pinning an unsupported codec.
   */
  mediaCodecOverride?: "h264" | "vp8" | "vp9" | null;
  /**
   * Per-camera role hint keyed by camera path (matches a `CameraEntry.path`):
   * - `board` — a digital-board / content camera (favour resolution/sharpness).
   * - `face`  — a presenter/face camera (favour framerate/latency).
   * - `auto`  — let the kiosk decide.
   *
   * Optional; unset/missing keys ⇒ `auto`. Unknown roles are dropped on resolve.
   */
  mediaCameraRoles?: Record<string, "board" | "face" | "auto">;
  /**
   * Per-camera explicit capture resolution/framerate override keyed by camera
   * path (matches a `CameraEntry.path`). Each entry pins the requested
   * `width`×`height`@`fps`. Optional; unset ⇒ the kiosk auto-selects. Corrupt /
   * out-of-range entries are dropped or clamped on resolve.
   */
  mediaResOverride?: Record<string, { width: number; height: number; fps: number }>;
}

export interface DeviceConfigResponse {
  ok: true;
  config: DeviceConfig;
}

export type DeviceConfigPatch = Partial<DeviceConfig>;

// ----- Flash firmware (Topic D) -----

/** Where the firmware image flashed to the device ESP comes from. */
export type FlashSource = "repo" | "custom";

/** Lifecycle status of an in-flight (or finished) flash run. */
export type FlashStatus = "running" | "ok" | "error";

/**
 * Admin → backend: trigger a flash of the prebuilt REPO firmware for the given
 * board. `force` is accepted for forward-compat but the flash always runs
 * (no checksum gate); esptool is always invoked.
 */
export interface FlashFirmwareRequest {
  source: FlashSource;
  board?: "esp8266" | "esp32";
  force?: boolean;
}

/**
 * Admin → backend: upload a CUSTOM firmware `.bin` (base64-encoded JSON, to
 * avoid a multipart dependency) and flash it instead of the repo image.
 * ESP32 custom images must be full/merged (flashed at offset 0x0).
 */
export interface FlashUploadRequest {
  firmware_b64: string;
  board?: "esp8266" | "esp32";
  filename?: string;
}

/** Backend → admin: ack for a flash trigger, carrying the new flash id. */
export interface FlashTriggerResponse {
  ok: true;
  flash_id: string;
}

/**
 * Backend → admin: incremental flash output. `next` is the cursor the admin
 * should pass as `since` on the next poll. `status` becomes "ok"/"error" when
 * the flash terminates; `rc` is the esptool exit code (null while running).
 */
export interface FlashLogResponse {
  ok: true;
  lines: string[];
  next: number;
  status: FlashStatus;
  rc: number | null;
}

/**
 * Agent → backend: a batch of esptool stdout lines for an in-flight flash.
 * `status`/`rc` are set on the terminal batch.
 */
export interface AgentFlashLogRequest {
  flash_id: string;
  lines: string[];
  status?: FlashStatus;
  rc?: number;
}

// ----- Feature toggles -----

export interface FeatureToggles {
  text_mode: boolean;
  raise_hand: boolean;
  student_video: boolean;
  student_audio: boolean;
  /**
   * E2EE scaffolding flag (Phase 5). DRIVEN BY GLOBAL SERVER CONFIG
   * (`E2EE_ENABLED`), surfaced here read-only so admin UIs can reflect it. The
   * authoritative source for frontends is `GET /e2ee/status`. Defaults FALSE;
   * when false the whole E2EE feature is inert. NOT a per-device toggle —
   * PATCH /admin/feature-toggles ignores this key.
   */
  e2ee_enabled: boolean;
}

export interface FeatureTogglesResponse {
  ok: true;
  toggles: FeatureToggles;
}

export type FeatureTogglesPatch = Partial<FeatureToggles>;

// ----- LED color overrides -----

export interface LedColorEntry {
  color: string;
  blink: boolean;
}

export type LedColorMap = Partial<Record<LedStateName, LedColorEntry>>;

export interface LedColorsResponse {
  ok: true;
  /** Admin overrides only — the agent merges with DEFAULT_LED_COLORS client-side. */
  overrides: LedColorMap;
}

// ----- Unified logging -----

/** One structured log line shipped by the local agent (device principal). */
export interface AgentLogLine {
  /** ms epoch; backend fills Date.now() when omitted. */
  ts?: number;
  level: string;
  logger?: string;
  msg: string;
  extra?: unknown;
}

export interface AgentLogRequest {
  lines: AgentLogLine[];
}

/** One structured log line shipped by a web client (student/admin/device kiosk). */
export interface ClientLogLine {
  /** ms epoch; backend fills Date.now() when omitted. */
  ts?: number;
  level: string;
  /** Originating subsystem, e.g. "web-student", "kiosk". */
  source: string;
  msg: string;
  stack?: string;
  url?: string;
  /** Correlates the line with an admin debug session. Optional (back-compat). */
  session_id?: string;
  /** Structured context bag. Optional (back-compat). */
  extra?: Record<string, unknown>;
}

export interface ClientLogRequest {
  lines: ClientLogLine[];
}

export interface LogIngestResponse {
  ok: true;
  accepted: number;
}

/**
 * Effective log level for a polling web client. `session_id` is the admin debug
 * session driving the level (null ⇒ standing/default). `poll_after_ms` is the
 * client's hint for when to re-poll.
 */
export interface ClientLogLevelResponse {
  ok: true;
  level: LogLevel;
  session_id: number | null;
  poll_after_ms: number;
}

/** A rotated/archived log file surfaced to admins. */
export interface LogArchiveInfo {
  filename: string;
  size: number;
  /** ms epoch of last modification. */
  mtime: number;
}

export interface ServerArchivesResponse {
  ok: true;
  archives: LogArchiveInfo[];
}

export interface DeviceArchivesResponse {
  ok: true;
  archives: LogArchiveInfo[];
}

/** Per-device logging summary for the admin devices view. */
export interface DeviceLogInfo {
  label: string;
  last_seen_at: number | null;
  /** Bytes in the device's current device.log (0 if none). */
  log_bytes: number;
  archive_count: number;
}

export interface DevicesLogListResponse {
  ok: true;
  devices: DeviceLogInfo[];
}

// ----- Diagnostic link mini-API -----

/** One normalized log entry surfaced by the diagnostic mini-API. */
export interface DiagLogEntry {
  ts: number;
  level: string;
  msg: string;
  src: string;
  device?: string;
  raw?: unknown;
}

/** Manifest / self-describing index for a diagnostic link. */
export interface DiagInfoResponse {
  ok: true;
  /** `user_id` scopes the link to a single user when present (additive). */
  scope: { device_label: string | null; user_id?: number | null };
  expires_at: number;
  devices: { label: string; last_seen_at: number | null }[];
  levels: string[];
  endpoints: { path: string; desc: string }[];
}

export interface DiagLogsResponse {
  ok: true;
  entries: DiagLogEntry[];
  /** Opaque backward-paging cursor (oldest ts seen minus 1) or null. */
  next_cursor: string | null;
}

export interface DiagSummaryResponse {
  ok: true;
  window: { since: number | null; until: number | null };
  totals: { error: number; warn: number };
  per_device: {
    label: string;
    error: number;
    warn: number;
    top_msgs: { msg: string; count: number }[];
  }[];
  server: { error: number; warn: number };
}

export interface DiagAuditResponse {
  ok: true;
  entries: AuditEntry[];
  next_cursor: number | null;
}

export interface DiagDevicesResponse {
  ok: true;
  devices: DashboardDeviceState[];
}

// ----- Diagnostic link admin management -----

export interface DiagLinkInfo {
  token: string;
  url: string;
  device_label: string | null;
  /** User this link is scoped to (user-scoped tokens), or null. Optional/additive. */
  scope_user_id?: number | null;
  created_at: number;
  expires_at: number;
  last_used_at: number | null;
  created_by: number | null;
}

export interface CreateDiagLinkRequest {
  device_label?: string | null;
  /** Scope the new token to a single user (user-scoped diagnostics). Optional. */
  scope_user_id?: number | null;
  /** Alias for `scope_user_id`; either may be sent. Optional. */
  user_id?: number | null;
  ttl_hours: number;
}

export interface CreateDiagLinkResponse {
  ok: true;
  link: DiagLinkInfo;
}

export interface DiagLinksResponse {
  ok: true;
  links: DiagLinkInfo[];
}

// ----- Log query / search + debug sessions (extended diag API) -----

/** What kind of principal a stored log line / debug session is scoped to. */
export type LogTargetType = "server" | "device" | "student" | "teacher" | "admin" | "kid";

/** One normalized log row returned by the extended log-search API. */
export interface LogEntryDTO {
  id: number;
  ts: number;
  target_type: LogTargetType;
  target_id: string | null;
  session_id: string | null;
  src: string | null;
  level: string;
  logger: string | null;
  msg: string;
  stack: string | null;
  url: string | null;
  extra: unknown;
}

export interface DiagSearchResponse {
  ok: true;
  entries: LogEntryDTO[];
  next_cursor: string | null;
}

/** Admin log-entries listing (cookie-authed Diagnostic-log UI). */
export interface LogEntriesResponse {
  ok: true;
  entries: LogEntryDTO[];
  next_cursor: string | null;
}

/** Admin error/warn summary over a window (Diagnostic-log UI). */
export interface LogSummaryResponse {
  ok: true;
  totals: { error: number; warn: number };
  per_target: Record<string, { error: number; warn: number }>;
  top_messages: { msg: string; count: number }[];
  window: { since: number | null; until: number | null };
}

/**
 * How a debug session's lifetime is bounded:
 * - `window`        — fixed [start_at, end_at] wall-clock window.
 * - `wallclock`     — runs until `end_at` regardless of activity.
 * - `online_budget` — consumes a budget of online seconds (`budget_seconds_total`).
 */
export type DebugSessionMode = "window" | "wallclock" | "online_budget";

/** A server-side debug session that raises a target's log level for a period. */
export interface DebugSessionDTO {
  id: number;
  target_type: LogTargetType;
  target_id: string;
  level: LogLevel;
  mode: DebugSessionMode;
  start_at: number | null;
  end_at: number | null;
  budget_seconds_total: number | null;
  budget_seconds_used: number;
  active: boolean;
  last_activity_ts: number | null;
  created_by: number | null;
  created_at: number;
  exhausted_at: number | null;
  stopped_at: number | null;
  note: string | null;
}

export interface CreateDebugSessionRequest {
  target_type: LogTargetType;
  target_id: string;
  level: LogLevel;
  mode: DebugSessionMode;
  start_at?: number;
  end_at?: number;
  budget_hours?: number;
  note?: string;
}

export interface DebugSessionResponse {
  ok: true;
  session: DebugSessionDTO;
}

export interface DebugSessionsResponse {
  ok: true;
  sessions: DebugSessionDTO[];
}

// ----- Device registry + actions -----

export interface DeviceListEntry {
  /** Public id (addressing key). */
  label: string;
  online: boolean;
  last_heartbeat_ts: number | null;
  last_heartbeat_age_s: number | null;
  agent_version: string | null;
  agent_state: string | null;
  /**
   * LED/on-air hardware link as of the last heartbeat. null = UNKNOWN (agent
   * too old, or no heartbeat yet) — render "unknown", never "connected".
   */
  led_connected: boolean | null;
  /** Last ms epoch the LED link was observed live; null = never/unknown. */
  led_seen_ms: number | null;
  system: AgentSystemInfo | null;
  /** Effective device log verbosity. Always provided by the backend. */
  loglevel: LogLevel;
  settings_pin: string | null;
  last_branch: string | null;
  created_at: number;
  last_seen_at: number | null;
  /** Filled in a later milestone; [] for now. */
  students_connected: string[];
  /** Exact release tag the device reported running, or null. */
  installed_tag: string | null;
  /** Commit sha the device reported running, or null. */
  installed_commit: string | null;
  /** Release/commit date the device reported (ISO string), or null. */
  release_time: string | null;
  /** Release channel = last_branch (LIVE/BETA/master), or null. */
  channel: string | null;
  /** Update availability vs the device's channel pointer. */
  update: DeviceUpdateInfo;
  /** Room this device lives in (1:N: room -> devices), or null when unassigned. */
  room_id: number | null;
  /** Resolved room name for display, or null. */
  room_name: string | null;
  /** E2EE: force encryption for this device (later phase). */
  force_encryption: boolean;
  /** E2EE: active key id for this device, or null (later phase). */
  e2ee_key_id: string | null;
  /** Soft pause: when true, auth (heartbeat/livekit) is blocked but record kept. */
  disabled: boolean;
  /**
   * When true, students joining the room this device lives in may NOT use
   * invisible mode (OR-ed with authority/school/room flags).
   */
  forbid_invisible: boolean;
  /**
   * When true, students/devices in the room this device lives in must perform
   * in-person QR key exchange (auto-seal disabled; OR-ed with
   * authority/school/room flags).
   */
  force_e2ee_qr: boolean;
  /**
   * When true, end-to-end encryption is REQUIRED for the room this device
   * lives in (no opt-out, unsupported viewers blocked); auto-seal stays
   * enabled (OR-ed with authority/school/room flags).
   */
  force_e2ee: boolean;
  /**
   * E2EE media-key rotation hour (0-23 local) for THIS device, or null =
   * inherit. The effective room value is the most-specific set level.
   */
  e2ee_rotate_hour: number | null;
}

/** Set a device's `forbid_invisible` flag. */
export interface SetDeviceForbidInvisibleRequest {
  forbid_invisible: boolean;
}

/** Set a device's `force_e2ee_qr` flag. */
export interface SetDeviceForceE2eeQrRequest {
  force_e2ee_qr: boolean;
}

/** Set a device's `force_e2ee` flag. */
export interface SetDeviceForceE2eeRequest {
  force_e2ee: boolean;
}

/** Set a device's `e2ee_rotate_hour` (0-23) or null to clear/inherit. */
export interface SetDeviceE2eeRotateHourRequest {
  e2ee_rotate_hour: number | null;
}

/** Assign / clear a device's room. `room_id: null` clears the assignment. */
export interface SetDeviceRoomRequest {
  room_id: number | null;
}

export interface AdminDevicesResponse {
  ok: true;
  devices: DeviceListEntry[];
  generated_at: number;
}

export interface RegisterDeviceRequest {
  label: string;
}

export interface RegisterDeviceResponse {
  ok: true;
  label: string;
  /** Secret bearer token; returned ONCE at registration. */
  token: string;
}

export interface DeviceActionResponse {
  ok: true;
  /** Enqueued pending_messages id (0 if none). */
  id: number;
}

export interface ChangeBranchRequest {
  channel: "LIVE" | "BETA" | "master" | "TESTING";
}

export interface ChangePinRequest {
  pin: string;
}

export interface RenameDeviceRequest {
  label: string;
}

export interface SetDeviceDisabledRequest {
  disabled: boolean;
}

// ----- Release management -----

/** A channel that names the release tag devices/infra should run. */
export type ReleaseChannel = "LIVE" | "BETA";

/** One release tag (vX.Y.Z) known to the git mirror. */
export interface ReleaseTagInfo {
  version: string;
  commit: string;
  date: string;
  message: string;
}

/** A channel pointer surfaced to the admin (and the public /channel API). */
export interface ChannelPointerInfo {
  channel: string;
  target_tag: string;
  commit: string | null;
  /** Tag/commit date from the mirror, or stored promotion time. */
  release_time: string | null;
}

export interface AdminReleasesResponse {
  ok: true;
  tags: ReleaseTagInfo[];
  pointers: {
    LIVE: ChannelPointerInfo | null;
    BETA: ChannelPointerInfo | null;
  };
  /** ISO timestamp of the last successful git-mirror fetch, or null. */
  last_synced: string | null;
  /** Number of release tags currently known to the mirror. */
  tag_count: number;
  /** Message of the last FAILED mirror fetch, null when the last one worked. */
  last_error: string | null;
}

export interface PromoteReleaseRequest {
  channel: ReleaseChannel;
  target_tag: string;
  force?: boolean;
}

export interface PromoteReleaseResponse {
  ok: true;
  channel: ReleaseChannel;
  target_tag: string;
}

/** Per-device update availability vs. its channel pointer. */
export interface DeviceUpdateInfo {
  available: boolean;
  target_tag: string | null;
  releases_behind: number | null;
  commits_behind: number | null;
}

export interface UpdateAllRequest {
  channel: string;
}

export interface UpdateAllResponse {
  ok: true;
  queued: number;
}

/** Public channel manifest (GET /channel/:name). */
export interface ChannelManifestResponse {
  channel: string;
  target_tag: string;
  commit: string | null;
  release_time: string | null;
}

// ----- Infra ops (admin) -----

export interface InfraGitStatusResponse {
  /** Authoritative identity of the RUNNING backend image (build-time injected). */
  running_version: string | null;
  running_commit: string | null;
  /** Live state of the host infra repo (INFRA_REPO_PATH). */
  deployed_tag: string | null;
  deployed_commit: string | null;
  deployed_at: string | null;
  channel: string | null;
  clean: boolean | null;
  /** Drift vs the resolved channel pointer. */
  releases_behind: number | null;
  commits_behind: number | null;
  target_tag: string | null;
  /** True only when the infra repo git reads succeeded (HEAD resolvable). */
  repo_ok: boolean;
  /** Per-field failure reasons; only keys that actually failed are present.
   *  Possible keys: deployed_tag, deployed_commit, deployed_at, channel, clean, drift. */
  errors?: Record<string, string>;
  /**
   * Can this backend drive `docker` itself (D47.2)?
   *
   * FALSE on a stock install: the host docker socket is root-equivalent and is
   * NOT mounted unless the operator opts in with `--enable-docker-socket`.
   * INFORMATIONAL ONLY since D48 — no admin route triggers an update any more.
   */
  docker_available: boolean;
}

export type InfraServiceStatus = "OK" | "WARN" | "FAIL";

export interface InfraServiceCheck {
  name: string;
  status: InfraServiceStatus;
  detail: string;
}

export interface InfraCheckAliveResult {
  ran_at: number;
  overall: InfraServiceStatus;
  services: InfraServiceCheck[];
  raw: string;
}

export type InfraCheckAliveResponse = InfraCheckAliveResult | { ran_at: null };

// NOTE (D48): infra UPDATE types (InfraSwitchChannelRequest,
// InfraStartUpdate*, InfraUpdate{Status,Log}Response, InfraUpdater*) were
// REMOVED with the admin-triggered updater. Updates are SSH-only via
// infra/update.sh. Read-only infra types above (git-status, check-alive) stay.

// ----- Admin settings (SMTP) -----

/**
 * Effective SMTP settings as surfaced to the admin UI. The password is NEVER
 * returned in plaintext — `pass_set` is a boolean "configured" indicator.
 * `port` is a string here (matches master's KV all-string contract).
 */
export interface SmtpSettings {
  host: string;
  port: string;
  secure: boolean;
  user: string;
  from: string;
  /** Whether an SMTP password is configured (stored or env). Never the value. */
  pass_set: boolean;
}

export interface SmtpSettingsResponse {
  ok: true;
  smtp: SmtpSettings;
}

/**
 * Partial update of the SMTP settings. Omitted fields are left unchanged. The
 * `pass` field is write-only: only applied when a non-empty string is sent
 * (so the masked UI can submit without clobbering the stored secret).
 */
export interface UpdateSmtpSettingsRequest {
  host?: string;
  port?: string;
  secure?: boolean;
  user?: string;
  from?: string;
  pass?: string;
}

/** Send a test email (and verify transport) to a chosen recipient. */
export interface SmtpTestRequest {
  to: string;
}

export interface SmtpTestResponse {
  ok: boolean;
  /** Error token when ok=false (e.g. "smtp_not_configured", "send_failed"). */
  error?: string;
}

// ----- Rooms (admin-facing) -----

/**
 * A room belongs to a school and may represent a CLASS NAME (need not be a
 * physical room). One room may have many devices (1:N). Teachers and students
 * are assigned per room.
 */
export interface AdminRoom {
  id: number;
  name: string;
  school_id: number;
  school_name?: string | null;
  description: string | null;
  visibility: RoomVisibility;
  online: boolean;
  /** Bound device labels living in this room (1:N). */
  devices: string[];
  /** Assigned teacher emails. */
  teachers: string[];
  /** Assigned student emails. */
  students: string[];
  created_at: number;
  /** Soft pause: when true, the room is hidden from kids and joins are blocked. */
  disabled: boolean;
  /** Forbid invisible mode for all students in this room. */
  forbid_invisible: boolean;
  /** Force QR key exchange for all devices/students in this room. */
  force_e2ee_qr: boolean;
  /** Require E2EE (auto-seal kept) for all devices/students in this room. */
  force_e2ee: boolean;
  /** E2EE media-key rotation hour (0-23 local), or null = inherit. */
  e2ee_rotate_hour: number | null;
}

export interface AdminRoomsResponse {
  ok: true;
  rooms: AdminRoom[];
}

/**
 * Live (in-memory) E2EE status of a single room, for the admin RoomDetail view.
 * Backs `GET /admin/rooms/:id/e2ee-status` (admin auth, e2eeEnabled-gated).
 * `encryption_mode` reflects the SAME whole-room downgrade decision the kiosk +
 * students negotiate; `"plaintext"` on a non-forced room means a participant's
 * device can't encrypt (the visible anomaly). `last_autoseal_failed` is the most
 * recent `e2ee_autoseal_failed` audit for this room, when one exists.
 */
export interface AdminRoomE2eeStatusResponse {
  ok: true;
  /** "encrypted" | "plaintext" — the live negotiated whole-room mode. */
  encryption_mode: "encrypted" | "plaintext";
  /** Most recent auto-seal failure for this room, when one has been recorded. */
  last_autoseal_failed?: {
    /** Numeric user id of the keyless student, when the audit recorded it. */
    user_id: number | null;
    /** Email of that user (joined), when resolvable. */
    user_email: string | null;
    /** Epoch ms the anomaly was recorded. */
    at: number;
  };
}

export interface CreateRoomRequest {
  name: string;
  school_id: number;
  visibility: RoomVisibility;
  description?: string | null;
}

export interface UpdateRoomRequest {
  name?: string;
  visibility?: RoomVisibility;
  description?: string | null;
  /** Soft pause: true hides from students + blocks join; record kept. */
  disabled?: boolean;
  /** Forbid invisible mode for all students in this room. */
  forbid_invisible?: boolean;
  /** Force QR key exchange for all devices/students in this room. */
  force_e2ee_qr?: boolean;
  /** Require E2EE (auto-seal kept) for all devices/students in this room. */
  force_e2ee?: boolean;
  /** E2EE media-key rotation hour (0-23), or null to clear/inherit. */
  e2ee_rotate_hour?: number | null;
}

/** Replace the full teacher set for a room (by email). */
export interface SetRoomTeachersRequest {
  emails: string[];
}

/** Replace the full student set for a room (by email). */
export interface SetRoomStudentsRequest {
  emails: string[];
}

// ----- Website settings (SMTP + notification recipients + Altcha) -----

/**
 * Admin settings as returned by `GET /admin/settings`. Non-secret values are
 * returned verbatim; secrets are NEVER echoed — only a boolean "configured"
 * indicator is exposed.
 */
export interface AdminSettings {
  smtp: {
    host: string;
    port: string;
    secure: boolean;
    user: string;
    from: string;
    /** Whether an SMTP password is configured (never the value). */
    passSet: boolean;
  };
  notify: {
    contact: string;
    shop: string;
    payment: string;
  };
  altcha: {
    /** Whether an Altcha HMAC key is configured (never the value). */
    hmacSet: boolean;
  };
  demo: {
    /**
     * Whether the public demo is enabled. Gates `POST /demo/token`, the demo
     * autologin, and the demo tenant/account seeding. Off by default
     * (prod-safe).
     */
    enabled: boolean;
  };
  beta: {
    /**
     * Whether the public beta phase is enabled. Gates the beta advertising and
     * the participation form on the public website. Off by default.
     */
    enabled: boolean;
  };
  /**
   * Editable footer appended to EVERY outgoing email. Localized: the recipient's
   * language (de/en) selects which footer is used. Effective values (stored or
   * the built-in default) are returned so the admin can see/edit the default.
   */
  mail: {
    footerDe: string;
    footerEn: string;
  };
}

export interface AdminSettingsResponse {
  ok: true;
  settings: AdminSettings;
}

/**
 * Partial update for `PATCH /admin/settings`. Only provided keys are written.
 * Secret fields (`smtp.pass`, `altcha.hmacKey`) are only overwritten when a
 * non-empty value is sent (omit / empty to keep the current secret).
 */
export interface PatchSettingsRequest {
  smtp?: {
    host?: string;
    port?: string;
    secure?: boolean;
    user?: string;
    from?: string;
    pass?: string;
  };
  notify?: {
    contact?: string;
    shop?: string;
    payment?: string;
  };
  altcha?: {
    hmacKey?: string;
  };
  demo?: {
    /** Enable/disable the public demo (token mint + demo autologin). */
    enabled?: boolean;
  };
  beta?: {
    /** Enable/disable the public beta (advertising + /beta-interest form). */
    enabled?: boolean;
  };
  /** Editable per-locale email footer appended to every outgoing email. */
  mail?: {
    footerDe?: string;
    footerEn?: string;
  };
}

export interface TestEmailRequest {
  /** Optional override recipient; defaults to `notify.contact`. */
  to?: string;
}

export interface TestEmailResponse {
  ok: boolean;
  /** Error token when ok=false. */
  error?: string;
}

export interface BackupInfo {
  filename: string;
  bytes: number;
  created_at: number; // epoch ms
}
export interface BackupListResponse {
  ok: true;
  backups: BackupInfo[];
}
export interface CreateBackupResponse {
  ok: true;
  backup: BackupInfo;
}
